import React from 'react';

import GuessCorrectnessBanner from '../shared/ContentBlocks/GuessCorrectnessBanner';

import CompletenessBanner from './CompletenessBanner';
import type {FrqGrading} from './frqGradingStatus';
import {frqCorrectness} from './frqGradingStatus';
import useFrqReleasedGrade from './hooks/useFrqReleasedGrade';
import ReleasedGradeView from './ReleasedGradeView.react';

type Props = {
  /** The question this submission answers, used to find the student's own guess. */
  questionId?: string;
  /** The question's rubric, as `question.getMeta().getQuestionRubric()` returns it. */
  rubric?: unknown;
  /**
   * The viewed guess's grading state, where a teacher-gated read supplies one. Absent in
   * independent practice and on a student's own view, which have no grading to report.
   */
  grading?: FrqGrading;
  /** Set where the response belongs to someone else — a teacher reading a student's submission. */
  isViewingStudentWork?: boolean;
};

/**
 * What sits above a submitted free response: the released grade once the student's teacher has
 * released it, the correctness banner once a grade exists, otherwise where the submission stands.
 *
 * A graded guess goes through the same `GuessCorrectnessBanner` as every other question type, so
 * correct / partially correct / incorrect is one component's job rather than an FRQ-only copy of
 * it. The status and grade are overridden rather than read off the legacy guess, for the same
 * reason `ReleasedGradeView` overrides them: free response is always scored as if partial credit
 * were enabled, and the rubric's grade is the one the teacher actually awarded.
 *
 * Owns the guess read so it stays inside the submitted branch — `FreeResponseQuestion` also mounts
 * in Dennis previews and other places with no Apollo client above it.
 *
 * Renders nothing while a grade could still arrive, so the banner is never shown over one.
 */
export default function SubmittedFrqStatus({
  questionId,
  rubric,
  grading,
  isViewingStudentWork
}: Props) {
  const {evaluation, isLoading} = useFrqReleasedGrade(questionId);

  if (isLoading) {
    return null;
  }

  if (evaluation) {
    return <ReleasedGradeView rubric={rubric} evaluation={evaluation} />;
  }

  if (grading?.status === 'published') {
    return (
      <GuessCorrectnessBanner
        partialCreditEnabled
        overriddenGuessStatus={frqCorrectness(grading.grade)}
        overriddenGrade={grading.grade}
      />
    );
  }

  return (
    <CompletenessBanner
      questionId={questionId}
      gradingStatus={grading?.status}
      isViewingStudentWork={isViewingStudentWork}
    />
  );
}
